/**
 * Exposure Apparel — design tool.
 *
 * Customers upload artwork and place it on the real product photo (front, back,
 * sleeves, legs). Print areas come from assets/ea-design-calibration.js and are
 * sized for the SMALLEST garment size, so one print size works for the whole order.
 *
 * DTF safeguards:
 *  - artwork can't leave the print area (drag + resize are clamped)
 *  - print size is capped so the file never drops below MIN_DPI at print size
 *  - minimum print size (MIN_CM on the longest side)
 *  - solid backgrounds detected, with one-click background removal
 *  - low contrast against the garment colour, soft edges / fades and overlaps flagged
 *  - file type / size / pixel checks on upload
 *
 * On save, each placed artwork becomes a `.print-location-card` in #eaDesignSummary,
 * so the existing builder prices it (getCustomisationCounts) and adds it to the cart
 * (custom-main-product.js reads card._eaPrint for the artwork, mockup and properties).
 */
(function () {
  'use strict';

  const CFG = {
    SMALL_MAX_CM: 12, // fits within 12 x 12 cm -> Small print price, otherwise Large
    MIN_CM: 3, // longest side of a print
    GOOD_DPI: 150,
    MIN_DPI: 100, // resizing stops here
    MAX_FILE_MB: 20,
    MIN_PX: 300, // longest side of an uploaded raster image
    MAX_PRINTS: 6,
    SNAP_CM: 0.6,
    ACCEPT: '.png,.jpg,.jpeg,.webp,.svg,.pdf,.ai,image/png,image/jpeg,image/webp,image/svg+xml,application/pdf,application/postscript',
    PDF_PREVIEW_PX: 2400, // longest side of the on-screen copy of a PDF page
  };

  const VIEW_LABELS = {
    front: 'Front',
    back: 'Back',
    'sleeve-left': 'Left sleeve',
    'sleeve-right': 'Right sleeve',
  };

  let data = null; // product data from snippets/ea-design-tool.liquid
  let cal = null; // calibration for this product
  let root = null;
  let saved = []; // items committed to the builder
  const state = { items: [], selectedId: null, view: 'front', colourIdx: 0, zoneId: null };
  let uid = 0;

  // ---------------------------------------------------------------------------
  // Setup
  // ---------------------------------------------------------------------------
  function readData() {
    const el = document.getElementById('ea-design-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function kindFor(type) {
    const t = String(type || '').toLowerCase();
    if (/pant|jogger|short|track/.test(t)) return 'pants';
    if (/hood/.test(t)) return 'hoodie';
    if (/tee|shirt|polo|crew|sweat|activewear|long sleeve|tank/.test(t)) return 'top';
    return null;
  }

  function getCalibration(d) {
    const all = window.EA_DESIGN_CAL;
    if (!all || !d) return null;
    if (all.products[d.handle]) return all.products[d.handle];
    const kind = kindFor(d.type);
    return kind ? all.defaults[kind] : null;
  }

  function isAvailable() {
    if (window.__CMP_BUILDER_CONFIG && window.__CMP_BUILDER_CONFIG.profile === 'hats_beanies') return false;
    data = data || readData();
    cal = cal || getCalibration(data);
    return !!(data && cal && data.colours && data.colours.length);
  }

  // Sleeves have no product photo, so they use a drawn sleeve in the garment colour.
  function sleeveZone(side) {
    const sl = cal.sleeve;
    const s = sl.long ? 0.0135 : 0.026; // stage fraction per cm
    const top = sl.long ? 0.1 + 5 * s : 0.2 + 3 * s;
    return {
      id: side,
      label: VIEW_LABELS[side],
      x: 0.5 - (sl.wCm * s) / 2,
      y: top,
      w: sl.wCm * s,
      h: sl.hCm * s,
      wCm: sl.wCm,
      hCm: sl.hCm,
    };
  }

  function viewsList() {
    const v = Object.keys(cal.views);
    if (cal.sleeve) v.push('sleeve-left', 'sleeve-right');
    return v;
  }

  function zonesFor(view) {
    if (view.startsWith('sleeve')) return [sleeveZone(view)];
    return cal.views[view] || [];
  }

  function zoneById(view, id) {
    return zonesFor(view).find((z) => z.id === id) || zonesFor(view)[0];
  }

  // ---------------------------------------------------------------------------
  // Garment colour sampling (for sleeve drawings + contrast checks)
  // ---------------------------------------------------------------------------
  const garmentColours = {};
  function sampleGarmentColour(idx) {
    if (garmentColours[idx]) return Promise.resolve(garmentColours[idx]);
    const c = data.colours[idx];
    return loadImage(c.thumb, true)
      .then((img) => {
        const cv = document.createElement('canvas');
        cv.width = 60;
        cv.height = 60;
        const ctx = cv.getContext('2d');
        ctx.drawImage(img, 0, 0, 60, 60);
        // sample the middle of the first front print area (chest, or a leg on pants)
        const z = zonesFor('front')[0];
        const px = ctx.getImageData(Math.round((z.x + z.w / 2) * 60) - 4, Math.round((z.y + z.h / 2) * 60) - 4, 8, 8).data;
        let r = 0, g = 0, b = 0, n = 0;
        for (let i = 0; i < px.length; i += 4) { r += px[i]; g += px[i + 1]; b += px[i + 2]; n++; }
        garmentColours[idx] = [Math.round(r / n), Math.round(g / n), Math.round(b / n)];
        return garmentColours[idx];
      })
      .catch(() => (garmentColours[idx] = [200, 200, 200]));
  }

  function rgbCss(rgb) {
    return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
  }

  function sleeveSvg(view, rgb) {
    const fill = rgbCss(rgb);
    const shade = rgbCss(rgb.map((v) => Math.max(0, v - 28)));
    const long = cal.sleeve.long;
    const path = long
      ? 'M 30 10 Q 50 4 70 10 L 64 86 L 36 86 Z'
      : 'M 18 20 Q 50 12 82 20 L 76 76 L 24 76 Z';
    const cuff = long ? '<path d="M 36 86 L 64 86 L 63 94 L 37 94 Z" fill="' + shade + '"/>' : '';
    const seam = long
      ? '<path d="M 31 14 Q 50 8 69 14" fill="none" stroke="' + shade + '" stroke-width="0.6"/>'
      : '<path d="M 24 72 L 76 72" fill="none" stroke="' + shade + '" stroke-width="0.6" stroke-dasharray="1.4 1"/>';
    const flip = view === 'sleeve-right' ? ' transform="translate(100 0) scale(-1 1)"' : '';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="1000" height="1000"><rect width="100" height="100" fill="#fbfbfb"/><g${flip}><path d="${path}" fill="${fill}" stroke="${shade}" stroke-width="0.5"/>${cuff}${seam}</g></svg>`;
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  }

  function viewImageSrc(view, idx) {
    const c = data.colours[idx];
    if (view === 'front') return c.front;
    if (view === 'back') return c.back || c.front;
    return sleeveSvg(view, garmentColours[idx] || [200, 200, 200]);
  }

  // ---------------------------------------------------------------------------
  // Image helpers
  // ---------------------------------------------------------------------------
  function loadImage(src, cors) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      if (cors) img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  function svgNaturalSize(text) {
    const vb = text.match(/viewBox\s*=\s*["']\s*[-\d.]+[\s,]+[-\d.]+[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
    if (vb) return [parseFloat(vb[1]), parseFloat(vb[2])];
    const w = text.match(/<svg[^>]*\swidth\s*=\s*["']([\d.]+)/i);
    const h = text.match(/<svg[^>]*\sheight\s*=\s*["']([\d.]+)/i);
    if (w && h) return [parseFloat(w[1]), parseFloat(h[1])];
    return [1000, 1000];
  }

  // Pixel analysis: transparency, solid background, soft edges, colour sample.
  function analyse(img, natW, natH) {
    const max = 360;
    const k = Math.min(1, max / Math.max(natW, natH));
    const w = Math.max(1, Math.round(natW * k));
    const h = Math.max(1, Math.round(natH * k));
    const cv = document.createElement('canvas');
    cv.width = w;
    cv.height = h;
    const ctx = cv.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    let px;
    try {
      px = ctx.getImageData(0, 0, w, h).data;
    } catch (e) {
      return null;
    }
    let transparent = 0, soft = 0, opaque = 0;
    const sample = [];
    const step = Math.max(1, Math.floor((w * h) / 2500));
    for (let i = 0, p = 0; i < px.length; i += 4, p++) {
      const a = px[i + 3];
      if (a < 10) transparent++;
      else {
        opaque++;
        if (a < 245) soft++;
        if (p % step === 0) sample.push([px[i], px[i + 1], px[i + 2]]);
      }
    }
    const total = w * h;
    const hasAlpha = transparent / total > 0.02;
    // Border uniformity
    const border = [];
    for (let x = 0; x < w; x++) { border.push(x * 4, ((h - 1) * w + x) * 4); }
    for (let y = 0; y < h; y++) { border.push(y * w * 4, (y * w + w - 1) * 4); }
    const med = [0, 1, 2].map((c) => {
      const vals = border.map((i) => px[i + c]).sort((a, b) => a - b);
      return vals[Math.floor(vals.length / 2)];
    });
    let near = 0;
    border.forEach((i) => {
      if (px[i + 3] > 200 && Math.max(Math.abs(px[i] - med[0]), Math.abs(px[i + 1] - med[1]), Math.abs(px[i + 2] - med[2])) < 22) near++;
    });
    const solidBg = !hasAlpha && near / border.length > 0.88;
    return {
      hasAlpha,
      solidBg,
      bg: med,
      photo: !hasAlpha && !solidBg,
      softPct: opaque ? soft / opaque : 0,
      sample,
    };
  }

  // Flood-fill the background from the edges and make it transparent.
  function removeBackground(item) {
    const maxSide = 4096;
    const k = Math.min(1, maxSide / Math.max(item.natW, item.natH));
    const w = Math.round(item.natW * k);
    const h = Math.round(item.natH * k);
    const cv = document.createElement('canvas');
    cv.width = w;
    cv.height = h;
    const ctx = cv.getContext('2d');
    ctx.drawImage(item.img, 0, 0, w, h);
    const id = ctx.getImageData(0, 0, w, h);
    const px = id.data;
    const bg = item.analysis.bg;
    const TOL = 34;
    const diff = (p) => Math.max(Math.abs(px[p] - bg[0]), Math.abs(px[p + 1] - bg[1]), Math.abs(px[p + 2] - bg[2]));
    const seen = new Uint8Array(w * h);
    const stack = new Int32Array(w * h);
    let sp = 0;
    const push = (i) => {
      if (!seen[i] && diff(i * 4) <= TOL) { seen[i] = 1; stack[sp++] = i; }
    };
    for (let x = 0; x < w; x++) { push(x); push((h - 1) * w + x); }
    for (let y = 0; y < h; y++) { push(y * w); push(y * w + w - 1); }
    while (sp) {
      const i = stack[--sp];
      px[i * 4 + 3] = 0;
      const x = i % w;
      if (x > 0) push(i - 1);
      if (x < w - 1) push(i + 1);
      if (i >= w) push(i - w);
      if (i < w * (h - 1)) push(i + w);
    }
    // Soften the cut edge so it doesn't look jagged
    for (let i = 0; i < w * h; i++) {
      if (seen[i]) continue;
      const x = i % w;
      const nb = (x > 0 && seen[i - 1]) || (x < w - 1 && seen[i + 1]) || (i >= w && seen[i - w]) || (i < w * (h - 1) && seen[i + w]);
      if (nb) {
        const d = diff(i * 4);
        if (d < TOL * 2.2) px[i * 4 + 3] = Math.round(255 * Math.min(1, (d - TOL) / (TOL * 1.2) + 0.15));
      }
    }
    ctx.putImageData(id, 0, 0);
    return new Promise((resolve) => cv.toBlob(resolve, 'image/png')).then((blob) => {
      const base = item.originalFile.name.replace(/\.[^.]+$/, '');
      return new File([blob], base + '-no-background.png', { type: 'image/png' });
    });
  }

  // ---------------------------------------------------------------------------
  // Geometry
  // ---------------------------------------------------------------------------
  const stageSize = () => (root ? root.querySelector('.ea-dt-stage').getBoundingClientRect().width : 500);
  const pxPerCm = (zone) => (zone.w * stageSize()) / zone.wCm;

  function maxPrintCm(item, zone) {
    // Largest w/h that fits the zone, keeps aspect, and stays >= MIN_DPI
    const ar = item.natW / item.natH;
    let w = Math.min(zone.wCm, zone.hCm * ar);
    if (!item.isSvg) w = Math.min(w, (item.natW / CFG.MIN_DPI) * 2.54);
    return { w, h: w / ar };
  }

  function minPrintCm(item) {
    const ar = item.natW / item.natH;
    return ar >= 1 ? { w: CFG.MIN_CM, h: CFG.MIN_CM / ar } : { w: CFG.MIN_CM * ar, h: CFG.MIN_CM };
  }

  function clampItem(item) {
    const zone = zoneById(item.view, item.zoneId);
    const ar = item.natW / item.natH;
    const mx = maxPrintCm(item, zone);
    const mn = minPrintCm(item);
    item.w = Math.max(Math.min(item.w, mx.w), Math.min(mn.w, mx.w));
    item.h = item.w / ar;
    item.x = Math.min(Math.max(0, item.x), zone.wCm - item.w);
    item.y = Math.min(Math.max(0, item.y), zone.hCm - item.h);
  }

  function dpiOf(item) {
    return item.isSvg ? Infinity : Math.round(item.natW / (item.w / 2.54));
  }

  // Price band from the printed size — shared rulebook (assets/ea-print-rules.js)
  function tierOf(item) {
    if (window.EA_PRINT_RULES) return window.EA_PRINT_RULES.bandFor('Digital Print', item.w, item.h);
    return item.w <= CFG.SMALL_MAX_CM + 0.05 && item.h <= CFG.SMALL_MAX_CM + 0.05 ? 'Small' : 'Large';
  }

  // Placement name, matching the builder's catalogue where possible
  function placementOf(item) {
    const zone = zoneById(item.view, item.zoneId);
    const small = tierOf(item) === 'Small';
    const cxOff = item.x + item.w / 2 - zone.wCm / 2; // + = viewer's right
    if (item.view === 'sleeve-left') return 'Left Sleeve';
    if (item.view === 'sleeve-right') return 'Right Sleeve';
    if (zone.id === 'leg-left') return 'Left Leg';
    if (zone.id === 'leg-right') return 'Right Leg';
    if (zone.id === 'front-left') return 'Front Left Chest';
    if (zone.id === 'front-right') return 'Front Right Chest';
    if (zone.id === 'front-lower-left') return 'Lower Left';
    if (zone.id === 'front-lower-right') return 'Lower Right';
    const yMid = (item.y + item.h / 2) / zone.hCm;
    if (item.view === 'front') {
      if (small && yMid < 0.45) {
        // Front view is drawn facing the wearer: viewer's right = wearer's left
        if (cxOff > 3) return 'Front Left Chest';
        if (cxOff < -3) return 'Front Right Chest';
        return 'Center Front';
      }
      if (yMid > 0.62) return small ? (cxOff > 3 ? 'Lower Left' : cxOff < -3 ? 'Lower Right' : 'Lower Front') : 'Lower Front';
      return 'Full Front';
    }
    if (item.view === 'back') {
      if (yMid > 0.62) return 'Lower Back';
      if (item.h <= 12 && item.y < zone.hCm * 0.25) return 'Top Back';
      return 'Center Back';
    }
    return zone.label;
  }

  function placementLabel(value) {
    const P = window.EAPrintPlacements;
    const spot = P && P.getSpot(value);
    return spot ? (spot.area === 'Front' || spot.area === 'Back' ? spot.area + ' – ' : '') + spot.label : value;
  }

  // Position readout relative to the collar / shoulder seam / waistband
  function positionText(item) {
    const zone = zoneById(item.view, item.zoneId);
    const isSleeve = item.view.startsWith('sleeve');
    const isPants = cal.kind === 'pants';
    let topCm;
    if (isSleeve) topCm = (zone.y - (cal.sleeve.long ? 0.1 : 0.2)) / (zone.w / zone.wCm) + item.y;
    else topCm = (zone.y - cal.hps) / (zone.w / zone.wCm) + item.y;
    const ref = isSleeve ? 'shoulder seam' : isPants ? 'waistband' : 'collar';
    const cx = item.x + item.w / 2;
    let horiz;
    if (isSleeve || isPants || zone.id.indexOf('front-') === 0) {
      horiz = Math.abs(cx - zone.wCm / 2) < 0.3 ? 'centred in its area' : `${Math.abs(cx - zone.wCm / 2).toFixed(1)} cm off-centre in its area`;
    } else {
      // Offset from the garment centre line
      const zoneCentreFromLine = (zone.x + zone.w / 2 - cal.cx) / (zone.w / zone.wCm);
      const off = cx - zone.wCm / 2 + zoneCentreFromLine;
      if (Math.abs(off) < 0.3) horiz = 'centred';
      else {
        const wearerLeft = item.view === 'front' ? off > 0 : off < 0;
        horiz = `${Math.abs(off).toFixed(1)} cm to wearer's ${wearerLeft ? 'left' : 'right'}`;
      }
    }
    return { top: `Top edge ${Math.max(0, topCm).toFixed(1)} cm below ${ref}`, horiz };
  }

  // ---------------------------------------------------------------------------
  // Checks
  // ---------------------------------------------------------------------------
  function colourDistance(a, b) {
    const rm = (a[0] + b[0]) / 2;
    const dr = a[0] - b[0], dg = a[1] - b[1], db = a[2] - b[2];
    return Math.sqrt((2 + rm / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rm) / 256) * db * db);
  }

  function lowContrast(item, idx) {
    const g = garmentColours[idx];
    const s = item.analysis && item.analysis.sample;
    if (!g || !s || !s.length) return false;
    if (item.analysis.solidBg || item.analysis.photo) return false; // prints as a block, always visible
    const close = s.filter((p) => colourDistance(p, g) < 70).length;
    return close / s.length > 0.6;
  }

  function overlaps(item) {
    return state.items.some((o) => {
      if (o === item || o.view !== item.view) return false;
      const za = zoneById(item.view, item.zoneId), zb = zoneById(o.view, o.zoneId);
      const k = (z) => z.w / z.wCm; // stage fraction per cm
      const ax = za.x + item.x * k(za), ay = za.y + item.y * k(za), aw = item.w * k(za), ah = item.h * k(za);
      const bx = zb.x + o.x * k(zb), by = zb.y + o.y * k(zb), bw = o.w * k(zb), bh = o.h * k(zb);
      return ax < bx + bw && bx < ax + aw && ay < by + bh && by < ay + ah;
    });
  }

  function checksFor(item) {
    const out = [];
    const dpi = dpiOf(item);
    const mx = maxPrintCm(item, zoneById(item.view, item.zoneId));
    if (item.isSvg) out.push({ level: 'ok', text: 'Vector file: sharp at any size.' });
    else if (dpi >= CFG.GOOD_DPI) out.push({ level: 'ok', text: `Print quality: great (${dpi} DPI at this size).` });
    else out.push({ level: 'warn', text: `Print quality: OK but may look slightly soft (${dpi} DPI). Make it smaller or upload a larger file for a crisper print.` });
    if (!item.isSvg && mx.w < zoneById(item.view, item.zoneId).wCm - 0.1 && Math.abs(item.w - mx.w) < 0.05) {
      out.push({ level: 'info', text: `This file can print up to ${mx.w.toFixed(1)} cm wide. Upload a larger file to go bigger.` });
    }
    const a = item.analysis;
    if (a) {
      if (a.hasAlpha) out.push({ level: 'ok', text: 'Transparent background: only your design will print.' });
      else if (a.solidBg) out.push({ level: 'warn', text: 'Your image has a solid background, which will print as a box around your design.', action: 'remove-bg' });
      else out.push({ level: 'info', text: 'Full-colour image with no transparent background: it will print as a rectangle.' });
      if (a.softPct > 0.15) out.push({ level: 'info', text: 'Soft edges, shadows or fades print as a fine dot pattern with DTF. Solid colours give the cleanest result.' });
    }
    if (lowContrast(item, state.colourIdx)) {
      out.push({ level: 'warn', text: `Much of your design is close to ${data.colours[state.colourIdx].name}, so it may be hard to see on this colour.` });
    }
    if (overlaps(item)) out.push({ level: 'warn', text: 'This print overlaps another print. Each is printed separately, so move them apart unless that is intended.' });
    out.push({ level: 'ok', text: 'Inside the print area, which fits every size you order.' });
    return out;
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------
  const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const ICONS = {
    ok: '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><circle cx="10" cy="10" r="9" fill="currentColor" opacity=".14"/><path d="M6 10.4l2.6 2.6L14 7.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    warn: '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M10 2.5l8 14.5H2z" fill="currentColor" opacity=".16"/><path d="M10 8v4M10 14.6v.1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    info: '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><circle cx="10" cy="10" r="9" fill="currentColor" opacity=".14"/><path d="M10 9v5M10 6.2v.1" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    upload: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>',
    close: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    trash: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg>',
  };

  function buildShell() {
    root = document.createElement('div');
    root.className = 'ea-dt';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-labelledby', 'eaDtTitle');
    root.hidden = true;
    root.innerHTML = `
      <div class="ea-dt__backdrop" data-dt-close></div>
      <div class="ea-dt__panel">
        <header class="ea-dt__header">
          <div>
            <h2 class="ea-dt__title" id="eaDtTitle">Design tool</h2>
            <p class="ea-dt__subtitle">${esc(data.title)} · upload your artwork and place it on the garment</p>
          </div>
          <button type="button" class="ea-dt__icon-btn" data-dt-close aria-label="Close design tool">${ICONS.close}</button>
        </header>
        <div class="ea-dt__body">
          <div class="ea-dt__canvas-col">
            <div class="ea-dt__views" role="tablist" aria-label="Garment side"></div>
            <div class="ea-dt-stage" tabindex="-1">
              <img class="ea-dt-stage__garment" alt="" draggable="false">
              <div class="ea-dt-stage__layer"></div>
              <div class="ea-dt-stage__drop" hidden><span>Drop your artwork here</span></div>
            </div>
            <div class="ea-dt__colours">
              <span class="ea-dt__colours-label">Preview on: <strong data-dt-colour-name></strong></span>
              <div class="ea-dt__swatches"></div>
            </div>
          </div>
          <div class="ea-dt__side">
            <div class="ea-dt__zones"></div>
            <label class="ea-dt__upload">
              <input type="file" accept="${CFG.ACCEPT}" data-dt-file hidden>
              <span class="ea-dt__upload-icon">${ICONS.upload}</span>
              <span class="ea-dt__upload-text">
                <strong>Upload artwork</strong>
                <span>PNG with a transparent background works best. PNG, JPG, WEBP, SVG, PDF or AI, up to ${CFG.MAX_FILE_MB} MB.</span>
              </span>
            </label>
            <p class="ea-dt__error" role="alert" hidden></p>
            <div class="ea-dt__inspector"></div>
            <details class="ea-dt__guide">
              <summary>Print guidelines</summary>
              <ul>
                <li>The dashed box is the print area. It is sized to fit the smallest size, so every garment in your order gets the same print.</li>
                <li>Print price depends on the printed size: ${window.EA_PRINT_RULES ? window.EA_PRINT_RULES.describeBands('Digital Print').join(', ') : `Small fits within ${CFG.SMALL_MAX_CM} × ${CFG.SMALL_MAX_CM} cm, anything bigger is Large`}.</li>
                <li>For a sharp print, use at least ${CFG.GOOD_DPI} DPI at print size (e.g. 1800 px wide for a 30 cm print). We stop resizing at ${CFG.MIN_DPI} DPI.</li>
                <li>Very thin lines (under 1 mm) and tiny text (under 6 pt) may not hold up in the wash.</li>
                <li>PDF and AI files (saved PDF-compatible) can be placed here too. We print from your original file; only page 1 is used.</li>
                <li>We send a digital proof to approve before anything is printed.</li>
              </ul>
            </details>
          </div>
        </div>
        <footer class="ea-dt__footer">
          <p class="ea-dt__summary" aria-live="polite"></p>
          <div class="ea-dt__actions">
            <button type="button" class="ea-dt__btn ea-dt__btn--ghost" data-dt-close>Cancel</button>
            <button type="button" class="ea-dt__btn ea-dt__btn--primary" data-dt-save>Save design</button>
          </div>
        </footer>
      </div>`;
    document.body.appendChild(root);

    root.addEventListener('click', onRootClick);
    root.querySelector('[data-dt-file]').addEventListener('change', (e) => {
      const f = e.target.files && e.target.files[0];
      e.target.value = '';
      if (f) addFile(f);
    });
    root.addEventListener('keydown', onKey);
    const stage = root.querySelector('.ea-dt-stage');
    const drop = root.querySelector('.ea-dt-stage__drop');
    ['dragenter', 'dragover'].forEach((t) => stage.addEventListener(t, (e) => { e.preventDefault(); drop.hidden = false; }));
    ['dragleave', 'drop'].forEach((t) => stage.addEventListener(t, (e) => { e.preventDefault(); if (t === 'dragleave' && stage.contains(e.relatedTarget)) return; drop.hidden = true; }));
    stage.addEventListener('drop', (e) => {
      const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) addFile(f);
    });
    stage.addEventListener('pointerdown', (e) => {
      if (e.target === stage || e.target.classList.contains('ea-dt-stage__garment') || e.target.classList.contains('ea-dt-stage__layer')) select(null);
    });
    window.addEventListener('resize', () => { if (!root.hidden) renderStage(); });
  }

  function renderViews() {
    const wrap = root.querySelector('.ea-dt__views');
    wrap.innerHTML = viewsList()
      .map((v) => {
        const n = state.items.filter((i) => i.view === v).length;
        return `<button type="button" role="tab" class="ea-dt__view${v === state.view ? ' is-active' : ''}" aria-selected="${v === state.view}" data-dt-view="${v}">${VIEW_LABELS[v] || v}${n ? ` <span class="ea-dt__count">${n}</span>` : ''}</button>`;
      })
      .join('');
  }

  function renderSwatches() {
    const wrap = root.querySelector('.ea-dt__swatches');
    wrap.innerHTML = data.colours
      .map((c, i) => `<button type="button" class="ea-dt__swatch${i === state.colourIdx ? ' is-active' : ''}" data-dt-colour="${i}" title="${esc(c.name)}" aria-label="Preview on ${esc(c.name)}" aria-pressed="${i === state.colourIdx}"><img src="${esc(c.thumb)}" alt="" width="40" height="40"></button>`)
      .join('');
    root.querySelector('[data-dt-colour-name]').textContent = data.colours[state.colourIdx].name;
  }

  function renderZonePicker() {
    const zones = zonesFor(state.view);
    const wrap = root.querySelector('.ea-dt__zones');
    if (zones.length < 2) { wrap.innerHTML = ''; return; }
    if (!zones.some((z) => z.id === state.zoneId)) state.zoneId = zones[0].id;
    wrap.innerHTML = `<p class="ea-dt__label">Print area for your next upload</p><div class="ea-dt__chips">${zones
      .map((z) => `<button type="button" class="ea-dt__chip${z.id === state.zoneId ? ' is-active' : ''}" data-dt-zone="${z.id}" aria-pressed="${z.id === state.zoneId}">${esc(z.label)} <span>${z.wCm} × ${z.hCm} cm</span></button>`)
      .join('')}</div>`;
  }

  function renderStage() {
    const stage = root.querySelector('.ea-dt-stage');
    const img = stage.querySelector('.ea-dt-stage__garment');
    const src = viewImageSrc(state.view, state.colourIdx);
    if (img.getAttribute('src') !== src) {
      img.src = src;
      img.alt = `${data.colours[state.colourIdx].name} – ${VIEW_LABELS[state.view]}`;
    }
    const S = stageSize();
    const layer = stage.querySelector('.ea-dt-stage__layer');
    const zones = zonesFor(state.view);
    const sel = state.items.find((i) => i.id === state.selectedId && i.view === state.view);
    const labelled = zones.length > 1 ? (sel ? sel.zoneId : state.zoneId) : zones[0] && zones[0].id;
    const zonesHtml = zones
      .map((z) => {
        const active = zones.length > 1 && z.id === state.zoneId;
        const label = z.id === labelled ? `<span class="ea-dt-zone__label">${esc(z.label)} · max ${z.wCm} × ${z.hCm} cm</span>` : '';
        return `<div class="ea-dt-zone${active ? ' is-active' : ''}${z.x + z.w / 2 > 0.5 ? ' ea-dt-zone--right' : ''}" style="left:${z.x * S}px;top:${z.y * S}px;width:${z.w * S}px;height:${z.h * S}px" data-dt-zone="${z.id}">${label}<span class="ea-dt-zone__guide" hidden></span></div>`;
      })
      .join('');
    layer.innerHTML = zonesHtml;
    state.items
      .filter((i) => i.view === state.view)
      .forEach((item) => {
        const z = zoneById(item.view, item.zoneId);
        const k = pxPerCm(z);
        const el = document.createElement('div');
        el.className = 'ea-dt-item' + (item.id === state.selectedId ? ' is-selected' : '');
        el.dataset.dtItem = item.id;
        el.tabIndex = 0;
        el.setAttribute('role', 'button');
        el.setAttribute('aria-label', `${item.originalFile.name}, ${item.w.toFixed(1)} by ${item.h.toFixed(1)} cm. Drag to move, arrow keys to nudge.`);
        el.style.cssText = `left:${z.x * S + item.x * k}px;top:${z.y * S + item.y * k}px;width:${item.w * k}px;height:${item.h * k}px`;
        el.innerHTML = `<img src="${item.src}" alt="" draggable="false">` +
          (item.id === state.selectedId ? ['nw', 'ne', 'sw', 'se'].map((c) => `<span class="ea-dt-handle ea-dt-handle--${c}" data-dt-handle="${c}"></span>`).join('') + `<span class="ea-dt-item__dims">${item.w.toFixed(1)} × ${item.h.toFixed(1)} cm</span>` : '');
        el.addEventListener('pointerdown', (e) => startDrag(e, item));
        el.addEventListener('focus', () => { if (state.selectedId !== item.id) select(item.id); });
        layer.appendChild(el);
      });
  }

  function renderInspector() {
    const wrap = root.querySelector('.ea-dt__inspector');
    const item = state.items.find((i) => i.id === state.selectedId);
    const forView = state.items.filter((i) => i.view === state.view);
    if (!item) {
      wrap.innerHTML = forView.length
        ? `<p class="ea-dt__hint">Select a print on the garment to resize or move it.</p>`
        : `<p class="ea-dt__hint">Upload artwork to place it on the ${esc((VIEW_LABELS[state.view] || '').toLowerCase())}. You can add prints to several sides. Each one is priced as its own print.</p>`;
      return;
    }
    const zone = zoneById(item.view, item.zoneId);
    const pos = positionText(item);
    const mx = maxPrintCm(item, zone);
    const mn = minPrintCm(item);
    const tier = tierOf(item);
    const presets = presetsFor(item);
    const checks = checksFor(item);
    wrap.innerHTML = `
      <div class="ea-dt-insp">
        <div class="ea-dt-insp__head">
          <div class="ea-dt-insp__thumb"><img src="${item.src}" alt=""></div>
          <div class="ea-dt-insp__meta">
            <strong title="${esc(item.file.name)}">${esc(item.file.name)}</strong>
            <span>${esc(placementLabel(placementOf(item)))} · <span class="ea-dt-tier ea-dt-tier--${tier.toLowerCase()}">${tier} print</span></span>
          </div>
          <button type="button" class="ea-dt__icon-btn ea-dt__icon-btn--danger" data-dt-delete aria-label="Remove this print">${ICONS.trash}</button>
        </div>
        <div class="ea-dt-insp__size">
          <label>Width <span class="ea-dt-num"><input type="number" step="0.5" min="${mn.w.toFixed(1)}" max="${mx.w.toFixed(1)}" value="${item.w.toFixed(1)}" data-dt-w inputmode="decimal"> cm</span></label>
          <label>Height <span class="ea-dt-num"><input type="number" step="0.5" min="${mn.h.toFixed(1)}" max="${mx.h.toFixed(1)}" value="${item.h.toFixed(1)}" data-dt-h inputmode="decimal"> cm</span></label>
        </div>
        <input type="range" class="ea-dt-insp__range" min="${mn.w}" max="${mx.w}" step="0.1" value="${item.w}" data-dt-range aria-label="Print size">
        <p class="ea-dt-insp__pos">${esc(pos.top)} · ${esc(pos.horiz)}</p>
        <div class="ea-dt__chips ea-dt__chips--presets">
          ${presets.map((p, i) => `<button type="button" class="ea-dt__chip" data-dt-preset="${i}">${esc(p.label)}</button>`).join('')}
        </div>
        ${zonesFor(item.view).length > 1 || viewsList().length > 1 ? `
        <label class="ea-dt-insp__move">Move to
          <select data-dt-move>
            ${viewsList().map((v) => zonesFor(v).map((z) => `<option value="${v}|${z.id}"${v === item.view && z.id === zone.id ? ' selected' : ''}>${esc(VIEW_LABELS[v])}${zonesFor(v).length > 1 ? ' – ' + esc(z.label) : ''}</option>`).join('')).join('')}
          </select>
        </label>` : ''}
        <ul class="ea-dt-checks">
          ${checks.map((c) => `<li class="ea-dt-check ea-dt-check--${c.level}">${ICONS[c.level]}<span>${esc(c.text)}${c.action === 'remove-bg' ? ' <button type="button" class="ea-dt__link" data-dt-remove-bg>Remove background</button>' : ''}</span></li>`).join('')}
          ${item.bgRemoved ? `<li class="ea-dt-check ea-dt-check--info">${ICONS.info}<span>Background removed automatically. Check the edges look right. <button type="button" class="ea-dt__link" data-dt-undo-bg>Undo</button></span></li>` : ''}
        </ul>
      </div>`;
  }

  function renderFooter() {
    const n = state.items.length;
    const counts = {};
    state.items.forEach((i) => { const t = tierOf(i); counts[t] = (counts[t] || 0) + 1; });
    const parts = ['Small', 'Medium', 'Large', 'XL'].filter((t) => counts[t]).map((t) => `${counts[t]} ${t === 'XL' ? 'XL' : t.toLowerCase()}`);
    root.querySelector('.ea-dt__summary').textContent = n
      ? `${n} print${n === 1 ? '' : 's'} (${parts.join(', ')}), added to every garment in your order.`
      : 'No prints yet.';
    const save = root.querySelector('[data-dt-save]');
    save.textContent = n ? 'Save design' : saved.length ? 'Save (remove all prints)' : 'Save design';
    save.disabled = !n && !saved.length;
  }

  function renderAll() {
    renderViews();
    renderZonePicker();
    renderStage();
    renderInspector();
    renderFooter();
  }

  function showError(msg) {
    const el = root.querySelector('.ea-dt__error');
    el.textContent = msg || '';
    el.hidden = !msg;
  }

  // ---------------------------------------------------------------------------
  // Presets
  // ---------------------------------------------------------------------------
  function presetsFor(item) {
    const zone = zoneById(item.view, item.zoneId);
    const list = [];
    const zcFromLine = item.view.startsWith('sleeve') || cal.kind === 'pants' ? 0 : (zone.x + zone.w / 2 - cal.cx) / (zone.w / zone.wCm);
    // cx is measured from the garment centre line; front view: + = wearer's left
    const P = (label, boxW, boxH, cxLine, top) => ({ label, boxW, boxH, cxLine, top });
    if (item.view === 'front' && zone.id === 'front') {
      if (zone.wCm >= 24) {
        list.push(P('Left chest', 9, 9, 9.5, 5), P('Centre chest', 10, 8, 0, 3), P('Full front', Math.min(28, zone.wCm), zone.hCm, 0, 2));
        if (cal.kind !== 'hoodie') list.push(P('Lower front', Math.min(24, zone.wCm), 10, 0, zone.hCm - 11));
      } else list.push(P('Centre chest', 10, 8, 0, 3), P('Fill area', zone.wCm, zone.hCm, 0, 0));
    } else if (item.view === 'back') {
      list.push(P('Upper back', Math.min(26, zone.wCm), 8, 0, 1), P('Full back', Math.min(30, zone.wCm), zone.hCm, 0, 3));
      if (cal.kind !== 'hoodie') list.push(P('Lower back', Math.min(24, zone.wCm), 10, 0, zone.hCm - 11));
    } else {
      list.push(P('Fill area', zone.wCm, zone.hCm, null, 0));
    }
    list.push(P('Centre', null, null, null, null));
    return list.map((p) => ({ ...p, zcFromLine }));
  }

  function applyPreset(item, p) {
    const zone = zoneById(item.view, item.zoneId);
    const ar = item.natW / item.natH;
    if (p.boxW) {
      item.w = Math.min(p.boxW, p.boxH * ar);
      item.h = item.w / ar;
    }
    const mirror = item.view === 'back' ? -1 : 1;
    if (p.cxLine === null || p.cxLine === undefined) item.x = (zone.wCm - item.w) / 2;
    else item.x = zone.wCm / 2 + mirror * p.cxLine - p.zcFromLine - item.w / 2;
    if (p.top !== null && p.top !== undefined) item.y = p.top;
    else item.y = (zone.hCm - item.h) / 2;
    clampItem(item);
  }

  // ---------------------------------------------------------------------------
  // Adding artwork
  // ---------------------------------------------------------------------------
  // PDF / AI support (pdf.js, Apache-2.0, loaded only when a PDF is uploaded).
  // The PDF itself is attached to the order unchanged; page 1 is only rasterised on
  // screen so it can be placed, sized and measured (mockup, price band, stitch estimate).
  let pdfjsPromise = null;
  function loadPdfjs() {
    if (!pdfjsPromise) {
      pdfjsPromise = import(data.pdfjs).then((pdfjs) => {
        pdfjs.GlobalWorkerOptions.workerSrc = data.pdfjsWorker;
        return pdfjs;
      }).catch((e) => { pdfjsPromise = null; throw e; });
    }
    return pdfjsPromise;
  }

  async function renderPdfPage(file, longestPx) {
    const pdfjs = await loadPdfjs();
    let doc;
    try {
      doc = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
    } catch (e) {
      const ai = /\.ai$/i.test(file.name || '');
      throw new Error(e && e.name === 'PasswordException'
        ? 'That PDF is password protected. Please upload an unlocked copy.'
        : ai
          ? "We couldn't read that AI file. In Illustrator, save it with \"Create PDF Compatible File\" ticked, or export a PDF."
          : "We couldn't read that PDF. Please try another file.");
    }
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const scale = longestPx / Math.max(base.width, base.height);
    const viewport = page.getViewport({ scale });
    const cv = document.createElement('canvas');
    cv.width = Math.max(1, Math.floor(viewport.width));
    cv.height = Math.max(1, Math.floor(viewport.height));
    await page.render({ canvas: cv, canvasContext: cv.getContext('2d'), viewport }).promise;
    const out = { canvas: cv, wPt: base.width, hPt: base.height, pages: doc.numPages };
    doc.destroy();
    return out;
  }

  function addPdf(file) {
    showError('Reading your PDF…');
    renderPdfPage(file, CFG.PDF_PREVIEW_PX)
      .then(({ canvas, wPt, hPt, pages }) => {
        showError('');
        const zones = zonesFor(state.view);
        const zone = zones.find((z) => z.id === state.zoneId) || zones[0];
        const item = {
          id: 'p' + ++uid,
          view: state.view,
          zoneId: zone.id,
          file,
          originalFile: file,
          src: canvas.toDataURL('image/png'),
          img: canvas,
          natW: wPt,
          natH: hPt,
          isSvg: true, // vector source: no DPI limit
          isPdf: true,
          pdfPages: pages,
          x: 0, y: 0, w: 1e6, h: 1e6,
          analysis: analyse(canvas, canvas.width, canvas.height),
        };
        state.items.push(item);
        const presets = presetsFor(item);
        const def = presets.find((p) => p.label === (state.view === 'back' ? 'Full back' : 'Centre chest')) || presets[0];
        applyPreset(item, def);
        select(item.id);
      })
      .catch((err) => showError(err && err.message && !/import|module|fetch/i.test(err.message) ? err.message : "We couldn't open PDFs on this device. Please export a PNG or SVG instead."));
  }

  function addFile(file) {
    showError('');
    if (state.items.length >= CFG.MAX_PRINTS) return showError(`You can add up to ${CFG.MAX_PRINTS} prints. Remove one first.`);
    const name = file.name || 'artwork';
    const ext = (name.split('.').pop() || '').toLowerCase();
    const isSvg = file.type === 'image/svg+xml' || ext === 'svg';
    if (/^(eps|psd)$/.test(ext)) {
      return showError(`.${ext.toUpperCase()} files can't be previewed here. Export a PDF, SVG or PNG to place it, or use "Choose your print location" to attach the ${ext.toUpperCase()} file.`);
    }
    if (/^(pdf|ai)$/.test(ext) || file.type === 'application/pdf') {
      if (file.size > CFG.MAX_FILE_MB * 1024 * 1024) return showError(`That file is ${(file.size / 1048576).toFixed(1)} MB. The limit is ${CFG.MAX_FILE_MB} MB.`);
      return addPdf(file);
    }
    if (!isSvg && !/^image\/(png|jpe?g|webp)$/.test(file.type) && !/^(png|jpe?g|webp)$/.test(ext)) {
      return showError('Please upload a PNG, JPG, WEBP, SVG, PDF or AI file.');
    }
    if (file.size > CFG.MAX_FILE_MB * 1024 * 1024) return showError(`That file is ${(file.size / 1048576).toFixed(1)} MB. The limit is ${CFG.MAX_FILE_MB} MB.`);

    const url = URL.createObjectURL(file);
    const textP = isSvg ? file.text() : Promise.resolve(null);
    Promise.all([loadImage(url), textP])
      .then(([img, text]) => {
        let natW = img.naturalWidth, natH = img.naturalHeight;
        if (isSvg) {
          const [w, h] = svgNaturalSize(text || '');
          natW = w; natH = h;
          // rasterise for consistent canvas use
          const scale = 1200 / Math.max(w, h);
          const cv = document.createElement('canvas');
          cv.width = Math.round(w * scale);
          cv.height = Math.round(h * scale);
          cv.getContext('2d').drawImage(img, 0, 0, cv.width, cv.height);
          img = cv;
        } else if (Math.max(natW, natH) < CFG.MIN_PX) {
          URL.revokeObjectURL(url);
          throw new Error(`That image is only ${natW} × ${natH} px, which is too small to print well. Please upload one at least ${CFG.MIN_PX} px on the longest side (ideally 2000 px+).`);
        }
        if (!natW || !natH) throw new Error("We couldn't read that file. Please try another.");
        const zones = zonesFor(state.view);
        const zone = zones.find((z) => z.id === state.zoneId) || zones[0];
        const item = {
          id: 'p' + ++uid,
          view: state.view,
          zoneId: zone.id,
          file,
          originalFile: file,
          src: url,
          img,
          natW,
          natH,
          isSvg,
          x: 0, y: 0, w: 1e6, h: 1e6,
          analysis: analyse(img, natW, natH),
        };
        state.items.push(item);
        // Default placement: a sensible size, not the full area
        const presets = presetsFor(item);
        const def = presets.find((p) => p.label === (state.view === 'back' ? 'Full back' : 'Centre chest')) || presets[0];
        applyPreset(item, def);
        select(item.id);
      })
      .catch((err) => showError(err && err.message ? err.message : "We couldn't read that image. Please try another file."));
  }

  // ---------------------------------------------------------------------------
  // Interaction
  // ---------------------------------------------------------------------------
  function select(id, keepFocus) {
    state.selectedId = id;
    renderStage();
    renderInspector();
    renderFooter();
    if (id && !keepFocus) {
      const el = root.querySelector(`[data-dt-item="${id}"]`);
      if (el) el.focus({ preventScroll: true });
    }
  }

  let drag = null;
  function startDrag(e, item) {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    if (state.selectedId !== item.id) select(item.id, true);
    const handle = e.target.dataset && e.target.dataset.dtHandle;
    const zone = zoneById(item.view, item.zoneId);
    drag = { item, zone, handle, sx: e.clientX, sy: e.clientY, x: item.x, y: item.y, w: item.w, h: item.h, k: pxPerCm(zone) };
    window.addEventListener('pointermove', onDrag);
    window.addEventListener('pointerup', endDrag, { once: true });
    window.addEventListener('pointercancel', endDrag, { once: true });
  }

  function onDrag(e) {
    if (!drag) return;
    const { item, zone, k } = drag;
    const dx = (e.clientX - drag.sx) / k;
    const dy = (e.clientY - drag.sy) / k;
    let guide = false;
    if (drag.handle) {
      const ar = item.natW / item.natH;
      const sx = drag.handle.includes('e') ? 1 : -1;
      const sy = drag.handle.includes('s') ? 1 : -1;
      // project the drag onto the diagonal so resizing feels natural from any corner
      const dw = Math.abs(sx * dx) > Math.abs(sy * dy * ar) ? sx * dx : sy * dy * ar;
      const mx = maxPrintCm(item, zone);
      const mn = minPrintCm(item);
      let w = Math.min(Math.max(drag.w + dw, mn.w), mx.w);
      // keep the opposite corner fixed, but stay within the area
      const right = drag.x + drag.w, bottom = drag.y + drag.h;
      const maxWByPos = Math.min(sx > 0 ? zone.wCm - drag.x : right, (sy > 0 ? zone.hCm - drag.y : bottom) * ar);
      w = Math.min(w, maxWByPos);
      item.w = w;
      item.h = w / ar;
      item.x = sx > 0 ? drag.x : right - item.w;
      item.y = sy > 0 ? drag.y : bottom - item.h;
    } else {
      item.x = drag.x + dx;
      item.y = drag.y + dy;
      const centre = (zone.wCm - item.w) / 2;
      if (Math.abs(item.x - centre) < CFG.SNAP_CM) { item.x = centre; guide = true; }
    }
    clampItem(item);
    // Fast update without re-rendering the panel
    const el = root.querySelector(`[data-dt-item="${item.id}"]`);
    const S = stageSize();
    if (el) {
      el.style.left = zone.x * S + item.x * k + 'px';
      el.style.top = zone.y * S + item.y * k + 'px';
      el.style.width = item.w * k + 'px';
      el.style.height = item.h * k + 'px';
      const dims = el.querySelector('.ea-dt-item__dims');
      if (dims) dims.textContent = `${item.w.toFixed(1)} × ${item.h.toFixed(1)} cm`;
    }
    const g = root.querySelector(`.ea-dt-zone[data-dt-zone="${zone.id}"] .ea-dt-zone__guide`);
    if (g) g.hidden = !guide;
  }

  function endDrag() {
    window.removeEventListener('pointermove', onDrag);
    drag = null;
    renderAll();
  }

  function onKey(e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    const item = state.items.find((i) => i.id === state.selectedId);
    if (!item || !e.target.closest || !e.target.closest('.ea-dt-item')) return;
    const step = e.shiftKey ? 2 : 0.5;
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (moves[e.key]) {
      e.preventDefault();
      item.x += moves[e.key][0];
      item.y += moves[e.key][1];
      clampItem(item);
      renderStage();
      renderInspector();
      const el = root.querySelector(`[data-dt-item="${item.id}"]`);
      if (el) el.focus({ preventScroll: true });
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      removeItem(item);
    }
  }

  function removeItem(item) {
    state.items = state.items.filter((i) => i !== item);
    if (!saved.some((s) => s.src === item.src) && item.src.startsWith('blob:')) URL.revokeObjectURL(item.src);
    state.selectedId = null;
    renderAll();
  }

  function setWidth(item, w) {
    item.w = w;
    item.h = w / (item.natW / item.natH);
    // keep centred on the same point while resizing from the inputs
    const cx = item.x + item.w / 2;
    clampItem(item);
    item.x = Math.min(Math.max(0, cx - item.w / 2), zoneById(item.view, item.zoneId).wCm - item.w);
    clampItem(item);
  }

  function onRootClick(e) {
    const t = e.target.closest('button, [data-dt-close]');
    if (!t || !root.contains(t)) return;
    if (t.hasAttribute('data-dt-close')) return close();
    if (t.hasAttribute('data-dt-save')) return save();
    if (t.dataset.dtView) {
      state.view = t.dataset.dtView;
      state.selectedId = null;
      showError('');
      return renderAll();
    }
    if (t.dataset.dtColour !== undefined) {
      state.colourIdx = Number(t.dataset.dtColour);
      sampleGarmentColour(state.colourIdx).then(() => { renderStage(); renderInspector(); });
      renderSwatches();
      renderStage();
      renderInspector();
      return;
    }
    if (t.dataset.dtZone && t.classList.contains('ea-dt__chip')) {
      state.zoneId = t.dataset.dtZone;
      renderZonePicker();
      renderStage();
      return;
    }
    const item = state.items.find((i) => i.id === state.selectedId);
    if (!item) return;
    if (t.hasAttribute('data-dt-delete')) return removeItem(item);
    if (t.dataset.dtPreset !== undefined) {
      applyPreset(item, presetsFor(item)[Number(t.dataset.dtPreset)]);
      return renderAll();
    }
    if (t.hasAttribute('data-dt-remove-bg')) {
      t.disabled = true;
      t.textContent = 'Removing…';
      removeBackground(item)
        .then((file) => {
          const url = URL.createObjectURL(file);
          return loadImage(url).then((img) => {
            item.prevSrc = item.src;
            item.file = file;
            item.src = url;
            item.img = img;
            item.analysis = analyse(img, item.natW, item.natH);
            item.bgRemoved = true;
            renderAll();
          });
        })
        .catch(() => showError("We couldn't remove the background automatically. Please upload a PNG with a transparent background."));
      return;
    }
    if (t.hasAttribute('data-dt-undo-bg')) {
      item.file = item.originalFile;
      item.src = item.prevSrc || URL.createObjectURL(item.originalFile);
      loadImage(item.src).then((img) => {
        item.img = img;
        item.analysis = analyse(img, item.natW, item.natH);
        item.bgRemoved = false;
        renderAll();
      });
    }
  }

  function onInspectorInput(e) {
    const item = state.items.find((i) => i.id === state.selectedId);
    if (!item) return;
    const t = e.target;
    const ar = item.natW / item.natH;
    if (t.matches('[data-dt-range]')) {
      setWidth(item, parseFloat(t.value));
      renderStage();
      const wIn = root.querySelector('[data-dt-w]'), hIn = root.querySelector('[data-dt-h]');
      if (wIn) wIn.value = item.w.toFixed(1);
      if (hIn) hIn.value = item.h.toFixed(1);
      if (e.type === 'change') renderAll();
      return;
    }
    if (e.type !== 'change') return;
    if (t.matches('[data-dt-w]') && t.value) setWidth(item, parseFloat(t.value));
    else if (t.matches('[data-dt-h]') && t.value) setWidth(item, parseFloat(t.value) * ar);
    else if (t.matches('[data-dt-move]')) {
      const [view, zoneId] = t.value.split('|');
      item.view = view;
      item.zoneId = zoneId;
      const p = presetsFor(item).find((x) => x.label === 'Centre');
      clampItem(item);
      applyPreset(item, { ...p, boxW: null });
      state.view = view;
    } else return;
    renderAll();
  }

  // ---------------------------------------------------------------------------
  // Mockups + save
  // ---------------------------------------------------------------------------
  function renderMockup(view, items) {
    const S = 1000;
    return loadImage(viewImageSrc(view, state.colourIdx), true)
      .then((garment) => {
        const cv = document.createElement('canvas');
        cv.width = S;
        cv.height = S;
        const ctx = cv.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, S, S);
        ctx.drawImage(garment, 0, 0, S, S);
        items.forEach((item) => {
          const z = zoneById(item.view, item.zoneId);
          const k = (z.w * S) / z.wCm;
          ctx.drawImage(item.img, z.x * S + item.x * k, z.y * S + item.y * k, item.w * k, item.h * k);
        });
        return new Promise((resolve) => cv.toBlob(resolve, 'image/jpeg', 0.86));
      })
      .then((blob) => (blob ? new File([blob], `mockup-${view}.jpg`, { type: 'image/jpeg' }) : null))
      .catch(() => null); // cross-origin photo → no mockup, the artwork still uploads
  }

  function save() {
    const btn = root.querySelector('[data-dt-save]');
    btn.disabled = true;
    btn.textContent = 'Saving…';
    const views = [...new Set(state.items.map((i) => i.view))];
    Promise.all(views.map((v) => renderMockup(v, state.items.filter((i) => i.view === v)))).then((mocks) => {
      const mockByView = {};
      views.forEach((v, i) => (mockByView[v] = mocks[i]));
      saved = state.items.slice();
      writeCards(mockByView);
      btn.disabled = false;
      close(true);
    });
  }

  function cardProps(item) {
    const pos = positionText(item);
    const props = {
      'Print side': VIEW_LABELS[item.view] || item.view,
      'Print size': `${item.w.toFixed(1)} × ${item.h.toFixed(1)} cm`,
      'Print position': `${pos.top}, ${pos.horiz}`,
      _design_tool: JSON.stringify({
        view: item.view,
        zone: item.zoneId,
        x_cm: +item.x.toFixed(2),
        y_cm: +item.y.toFixed(2),
        w_cm: +item.w.toFixed(2),
        h_cm: +item.h.toFixed(2),
        dpi: item.isPdf ? 'vector (pdf)' : item.isSvg ? 'vector' : dpiOf(item),
        source: item.isPdf ? 'pdf' : item.isSvg ? 'svg' : 'raster',
        bg_removed: !!item.bgRemoved,
        preview_colour: data.colours[state.colourIdx].name,
      }),
    };
    if (item.analysis && item.analysis.solidBg && !item.bgRemoved) props['Artwork note'] = 'Customer kept the solid background';
    return props;
  }

  function writeCards(mockByView) {
    const wrap = document.getElementById('eaDesignSummary');
    if (!wrap) return;
    wrap.querySelectorAll('.ea-dt-card').forEach((c) => c.remove());
    const variants = window.CUSTOMISATION_VARIANTS || {};
    saved.forEach((item, index) => {
      const placement = placementOf(item);
      const size = tierOf(item);
      const variantId = variants['Digital Print'] && variants['Digital Print'][size];
      const card = document.createElement('div');
      card.className = 'print-location-card ea-dt-card';
      card.dataset.eaDtId = item.id;
      const mock = mockByView[item.view];
      const thumb = mock ? URL.createObjectURL(mock) : item.src;
      card.innerHTML = `
        <input type="radio" name="eadt-${item.id}_printing_method" value="Digital Print" checked hidden>
        <input type="radio" name="eadt-${item.id}_spot" value="${esc(placement)}" checked hidden
          data-location-size="${size}" data-customisation-size="${size}" data-customisation-method="Digital Print"
          ${variantId ? `data-customisation-variant-id="${variantId}"` : ''}>
        <img class="ea-dt-card__thumb" src="${thumb}" alt="" width="64" height="64">
        <div class="ea-dt-card__info">
          <strong>Print ${index + 1}: ${esc(placementLabel(placement))}</strong>
          <span>Digital print · ${size} · ${item.w.toFixed(1)} × ${item.h.toFixed(1)} cm</span>
          <span class="ea-dt-card__file">${esc(item.file.name)}</span>
        </div>`;
      card._eaPrint = { file: item.file, original: item.originalFile, mockup: mock, props: cardProps(item) };
      wrap.appendChild(card);
    });
    const actions = document.createElement('div');
    actions.className = 'ea-dt-card ea-dt-summary__actions';
    actions.innerHTML = saved.length
      ? `<button type="button" class="ea-dt__btn ea-dt__btn--ghost" data-dt-edit>Edit design</button><button type="button" class="ea-dt__link" data-dt-clear>Remove all prints</button>`
      : '';
    wrap.appendChild(actions);
    wrap.hidden = !saved.length;
    // Let the builder recalculate prices
    if (window.recalculatePricing) window.recalculatePricing();
    if (window.updateDisplays && window.getSliderQuantity) window.updateDisplays(window.getSliderQuantity());
    document.dispatchEvent(new CustomEvent('ea:design-saved', { detail: { prints: saved.length } }));
  }

  // ---------------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------------
  let lastFocus = null;
  function open() {
    if (!isAvailable()) return false;
    if (!root) {
      buildShell();
      root.querySelector('.ea-dt__inspector').addEventListener('input', onInspectorInput);
      root.querySelector('.ea-dt__inspector').addEventListener('change', onInspectorInput);
    }
    // Work on a copy so Cancel throws away unsaved changes
    state.items = saved.map((i) => ({ ...i }));
    state.selectedId = null;
    if (!viewsList().includes(state.view)) state.view = viewsList()[0];
    lastFocus = document.activeElement;
    root.hidden = false;
    document.documentElement.classList.add('ea-dt-open');
    renderSwatches();
    showError('');
    sampleGarmentColour(state.colourIdx).then(() => { if (!root.hidden) { renderStage(); renderInspector(); } });
    renderAll();
    requestAnimationFrame(renderStage); // stage size is only known once visible
    setTimeout(() => root.querySelector('.ea-dt__header [data-dt-close]').focus(), 30);
    return true;
  }

  function close(didSave) {
    if (!root || root.hidden) return;
    if (!didSave) {
      // discard edits: restore saved objects
      state.items = saved.slice();
    } else {
      saved = state.items.slice();
    }
    root.hidden = true;
    document.documentElement.classList.remove('ea-dt-open');
    const btn = document.getElementById('designNowBtn');
    if (btn) btn.classList.toggle('active', saved.length > 0);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  // Summary card buttons (outside the modal)
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-dt-edit]')) open();
    if (e.target.closest('[data-dt-clear]')) {
      saved = [];
      state.items = [];
      writeCards({});
      const btn = document.getElementById('designNowBtn');
      if (btn) btn.classList.remove('active');
    }
  });

  // Customer changes colour in the builder → preview on that colour next time
  document.addEventListener('change', (e) => {
    if (!data || !e.target || !e.target.name || !/colou?r/i.test(e.target.name)) return;
    const i = data.colours.findIndex((c) => c.name === e.target.value);
    if (i >= 0) state.colourIdx = i;
  });

  window.EADesignTool = { open, close, isAvailable, config: CFG };
})();
